import { Fancybox } from '@fancyapps/ui/dist/fancybox/';
import Masonry from 'masonry-layout';
import imagesLoaded from 'imagesloaded';

export function initGallery(): void {
    Fancybox.bind('[data-fancybox]', {
        Carousel: {
            Toolbar: {
                display: {
                    left: ['counter'],
                    middle: ['zoomIn', 'zoomOut', 'toggle1to1'],
                    right: ['autoplay', 'thumbs', 'close'],
                },
            },
        },
    });

    const galleries = Array.from(document.querySelectorAll<HTMLElement>('.image-gallery'));
    if (!galleries.length) {
        return;
    }

    const masonryByGrid = new WeakMap<Element, Masonry>();

    const initMasonryForSection = (section: HTMLElement): Masonry | null => {
        if (!section) return null;
        const grid = section.querySelector<HTMLElement>('.image-gallery__images');
        if (!grid) return null;

        if (masonryByGrid.has(grid)) {
            return masonryByGrid.get(grid)!;
        }

        const msnry = new Masonry(grid, {
            itemSelector: '.image-gallery__item',
            columnWidth: '.image-gallery__item',
            percentPosition: true,
        });

        msnry.on('layoutComplete', () => {
            section.classList.add('is-ready');
        });

        imagesLoaded(grid).on('progress', () => {
            msnry.layout();
        });

        masonryByGrid.set(grid, msnry);
        return msnry;
    };

    const galleryIndexButtons = Array.from(
        document.querySelectorAll<HTMLButtonElement>('.gallery-page__index button[data-target]'),
    );
    const groupedGalleriesContainer = document.getElementById('LDSSiteGalleryImages');
    const groupedGalleries = groupedGalleriesContainer
        ? Array.from(groupedGalleriesContainer.querySelectorAll<HTMLElement>('.image-gallery'))
        : [];

    const findGalleryById = (targetId: string | null): HTMLElement | null => {
        if (!targetId) return null;
        return (
            groupedGalleries.find((section) => {
                const galleryId = section.dataset.galleryId || section.id;
                return galleryId === targetId;
            }) || document.getElementById(targetId)
        );
    };

    if (galleryIndexButtons.length && groupedGalleries.length) {
        const standaloneGalleries = galleries.filter(
            (section) => !groupedGalleries.includes(section),
        );
        standaloneGalleries.forEach((section) => initMasonryForSection(section));

        const activateButton = (activeButton: HTMLButtonElement): void => {
            galleryIndexButtons.forEach((button) => {
                const isActive = button === activeButton;
                button.classList.toggle('active', isActive);
                button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        };

        const toggleGalleryVisibility = (section: HTMLElement, isActive: boolean): void => {
            section.hidden = !isActive;
            section.setAttribute('aria-hidden', isActive ? 'false' : 'true');
            section.classList.toggle('active', isActive);
        };

        const showGallery = (targetSection: HTMLElement | null): void => {
            if (!targetSection) return;

            groupedGalleries.forEach((section) => {
                const isActive = section === targetSection;
                toggleGalleryVisibility(section, isActive);

                if (isActive) {
                    const masonryInstance = initMasonryForSection(section);
                    if (masonryInstance) {
                        masonryInstance.layout();
                    }
                }
            });
        };

        galleryIndexButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const targetId = button.dataset.target || button.getAttribute('aria-controls');
                const targetSection = findGalleryById(targetId ?? null);
                if (!targetSection) return;
                activateButton(button);
                showGallery(targetSection);
            });
        });

        const initialButton =
            galleryIndexButtons.find((button) => button.classList.contains('active')) ||
            galleryIndexButtons[0];
        if (initialButton) {
            activateButton(initialButton);
            const initialSection = findGalleryById(
                initialButton.dataset.target ||
                    initialButton.getAttribute('aria-controls') ||
                    null,
            );
            showGallery(initialSection);
        }

        return;
    }

    galleries.forEach((section) => initMasonryForSection(section));
}
